import Back from 'components/Back';
import {
  Button,
  Card,
  Container,
  DoAndDontList,
  SummaryList,
} from 'nhsuk-react-components';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useFetchTeamMembers from 'queries/fetch/fetchTeamMembers.ts';
import ProgressIndicator from 'components/ProgressIndicator';
import { IManageUserPageParams } from 'utils/parameterUtils.ts';
import { getMissingRolesForUser, getRolesForUser } from 'utils/userPermissions.ts';
import useFetchTeamDetails from 'queries/fetch/fetchTeamDetails';

function ManageUserDetailsPage() {
  const navigate = useNavigate();
  const { teamId, userId } = useParams() as IManageUserPageParams;
  const teamMembers = useFetchTeamMembers();
  const teamDetails = useFetchTeamDetails({ teamId });
  const currentUser = teamMembers.data?.[teamId].read.find((t) => t.sub === userId);

  if (!teamMembers.data || !currentUser || teamMembers.isRefetching || teamDetails.isFetching) {
    return <ProgressIndicator />;
  }

  const teamName = teamDetails.data?.team?.TEAM_NAME ?? teamId;

  const rolesForUser = getRolesForUser(currentUser, teamMembers.data[teamId]);
  const missingRolesForUser = getMissingRolesForUser(currentUser, teamMembers.data[teamId]);

  return (
    <Container>
      <div className="nhsuk-u-margin-top-6 nhsuk-u-margin-bottom-6">
        <Back />
      </div>
      <Card>
        <>
          <h2 className="nhsuk-u-margin-bottom-4">User details (global)</h2>
          <p className="nhsuk-u-secondary-text-colour">Your user details are managed globally and will be updated across all of your teams</p>
          <SummaryList>
            <SummaryList.Row>
              <SummaryList.Key>
                First name
              </SummaryList.Key>
              <SummaryList.Value>
                {currentUser?.given_name}
              </SummaryList.Value>
              <SummaryList.Action>
                <Link to={`/admin/${teamId}/manage-user-details/user/${userId}/details`}>Edit</Link>
              </SummaryList.Action>
            </SummaryList.Row>
            <SummaryList.Row>
              <SummaryList.Key>
                Last name
              </SummaryList.Key>
              <SummaryList.Value>
                {currentUser?.family_name}
              </SummaryList.Value>
              <SummaryList.Action>
                <Link to={`/admin/${teamId}/manage-user-details/user/${userId}/details`}>Edit</Link>
              </SummaryList.Action>
            </SummaryList.Row>
            <SummaryList.Row>
              <SummaryList.Key>
                Email
              </SummaryList.Key>
              <SummaryList.Value>
                {currentUser?.email}
              </SummaryList.Value>
            </SummaryList.Row>
          </SummaryList>

          <div style={{ position: 'relative' }}>

            <div className="nhsuk-grid-row">
              <div className="nhsuk-grid-column-two-thirds">
                <h3 className="nhsuk-u-margin-bottom-4">
                  User permissions (
                  {teamName}
                  )
                </h3>

                <p className="nhsuk-u-secondary-text-colour">
                  Your permissions apply only to the selected team and won’t affect your
                  access in other teams
                </p>
              </div>

              <div style={{ position: 'absolute', right: 0, top: 0 }}>
                <Button
                  secondary
                  onClick={() => {
                    navigate(`/admin/${teamId}/manage-user-details/user/${userId}/permissions`);
                  }}
                >
                  Edit permissions
                </Button>
              </div>
            </div>
            <DoAndDontList
              heading="Can"
              listType="do"
            >
              {rolesForUser
                .map(([_, description]) => (
                  <DoAndDontList.Item key={description}>
                    {description}
                  </DoAndDontList.Item>
                ))}
            </DoAndDontList>

            <DoAndDontList
              heading="Can't"
              listType="dont"
            >
              {missingRolesForUser
                .map(([_, description]) => (
                  <DoAndDontList.Item prefixText="cannot " key={description}>
                    {description}
                  </DoAndDontList.Item>
                ))}
            </DoAndDontList>
          </div>
        </>
      </Card>

      <Button onClick={() => navigate('/admin')}>
        Save and continue
      </Button>
    </Container>
  );
}

export default ManageUserDetailsPage;
